import React from 'react';
import clsx from 'clsx';
import styles from './Field.module.scss';

/**
 * Field
 *
 * Wraps a single form control (Input, Textarea, etc.) with:
 * - Label
 * - Hint / help text
 * - Error message
 * - Accessibility wiring (aria-describedby, aria-invalid)
 *
 * Props:
 * - label: string (optional)
 * - hint: string (optional)
 * - error: string (optional)
 * - required: boolean (optional)
 * - children: single React element (Input, Textarea, etc.)
 * - id: string (optional, autogenerated if not provided)
 */
const Field = ({
  label,
  hint,
  error,
  required,
  children,
  id: providedId,
  className,
}) => {
  // Generate a unique ID if none provided
  const id = providedId || `field-${Math.random().toString(36).substr(2, 9)}`;

  // IDs for accessibility
  const hintId = hint ? `${id}-hint` : undefined;
  const errorId = error ? `${id}-error` : undefined;

  // Combine aria-describedby
  const ariaDescribedBy = [hintId, errorId].filter(Boolean).join(' ') || undefined;

  const childWithProps = React.isValidElement(children)
    ? React.cloneElement(children, {
        id,
        'aria-describedby': ariaDescribedBy,
        'aria-invalid': !!error,
        variant: error ? 'error' : children.props.variant,
      })
    : children;

  return (
    <div className={clsx(styles.field, className)}>
      {label && (
        <label htmlFor={id} className={styles.label}>
          {label}
          {required && <span className={styles.required}>*</span>}
        </label>
      )}

      {childWithProps}

      {hint && !error && <div id={hintId} className={styles.hint}>{hint}</div>}
      {error && <div id={errorId} className={styles.error}>{error}</div>}
    </div>
  );
}

export default Field;
